import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_MAP,
  HIRING_CAMPAIGN_STATUSES,
  HIRING_CAMPAIGN_STATUS_MAP,
  HIRING_CHANNEL_DEFS,
  HIRING_CHANNEL_MAP,
  MAX_ANNOUNCEMENT,
  MAX_FORM_FIELDS,
  draftAnnouncement,
  jobApplyUrl,
  type CustomFieldType,
  type HiringChannel,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import { useAuth, useSession } from '../../auth/AuthProvider';

/* ==========================================================================
   الأنواع كما يردّها الخادم
   ========================================================================== */

interface StageCount {
  key: string;
  label: string;
  kind: string;
  /** ما يُنتظر فعلُه في هذا العمود — من ضبط الشركة لا من كتالوجٍ ثابت. */
  action: string | null;
  /** عمودٌ أُوقف وما زال فيه أحد. */
  stopped?: boolean;
  count: number;
}

interface RecruitSummary {
  openJobs: number;
  openings: number;
  totalApplications: number;
  active: number;
  waitlist: number;
  hired: number;
  hireRate: number | null;
  hireRateNote: string | null;
  stages: StageCount[];
}

interface CampaignRow {
  id: string;
  name: string;
  goal: number;
  status: keyof typeof HIRING_CAMPAIGN_STATUS_MAP;
  statusLabel: string;
  statusKind: string;
  startsOn: string | null;
  endsOn: string | null;
  notes: string | null;
  purpose: string | null;
  budget: number | null;
  channels: HiringChannel[];
  ownerId: string | null;
  jobCount: number;
  openings: number;
  applications: number;
}

interface FormField {
  id: string;
  key: string;
  label: string;
  type: string;
  options: string[];
  required: boolean;
  hint: string | null;
  position: number;
  isActive: boolean;
}

interface FormRow {
  id: string;
  name: string;
  intro: string | null;
  announcement: string | null;
  isActive: boolean;
  isDefault: boolean;
  jobCount: number;
  jobs: { id: string; title: string; slug: string; status: string }[];
  fields: FormField[];
}

/** ما يُقرأ من ملفّ الوظيفة ليُصاغ منه المنشور. */
interface JobFile {
  id: string;
  title: string;
  slug: string;
  department: string | null;
  employmentLabel: string;
  workModeLabel: string | null;
  location: string | null;
  description: string | null;
  duties: string[];
  techRequirements: string[];
  benefits: string[];
  experienceMin: number | null;
  educationLabel: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  showSalary: boolean;
  closesOn: string | null;
}

/* ==========================================================================
   نظرة على التوظيف
   ========================================================================== */

/**
 * الصفحة الأولى في القسم.
 *
 * تُقرأ من أعلى إلى أسفل كسؤالٍ واحد: ما الذي أبحث عنه، ومن وصلني، وأين
 * وقف. والقمعُ يُعرض بأعمدةٍ نسبيّةٍ لا بأرقامٍ مجرّدة — التكدّس يُرى قبل
 * أن يُحسب.
 */
export function RecruitOverview() {
  const summary = useQuery({
    queryKey: ['recruit', 'summary'],
    queryFn: () => api<RecruitSummary>('/hr/recruit/summary'),
  });

  const s = summary.data;
  const peak = s ? Math.max(1, ...s.stages.map((x) => x.count)) : 1;

  return (
    <div className="page">
      <PageHead
        title="التوظيف"
        subtitle="الشواغر المفتوحة ومن تقدّم لها وأين وقف كلٌّ منهم"
        actions={
          <Link className="btn btn-primary" to="/hr/recruit/jobs">
            <Icon name="briefcase" size={15} />
            الوظائف
          </Link>
        }
      />

      {summary.isError && (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      )}

      {summary.isPending && <div className="skeleton" style={{ height: 96 }} />}

      {s && (
        <>
          <div className="stats">
            <Stat
              label="وظائف مفتوحة"
              value={num(s.openJobs)}
              note={`${plural(s.openings, WORDS.vacancy)} في المجموع`}
            />
            <Stat
              label="طلبات قائمة"
              value={num(s.active)}
              note={`من ${plural(s.totalApplications, WORDS.application)}`}
            />
            <Stat
              label="قائمة الانتظار"
              value={num(s.waitlist)}
              note="مؤهَّلون بلا شاغر"
            />
            <Stat
              label="عُيّنوا"
              value={num(s.hired)}
              note={
                s.hireRate === null
                  ? (s.hireRateNote ?? undefined)
                  : `${s.hireRate}% من القرارات`
              }
            />
          </div>

          <div className="card">
            <h2 className="card-title">المسار</h2>
            <p className="tiny mute">
              الطلبات القائمة وحدها. من عُيّن أو استُبعد خرج من العدّ.
            </p>

            {s.active === 0 ? (
              <EmptyState
                title="لا طلبات قائمة"
                text="افتح وظيفةً وانشر رابطها، أو أدخل من وصلك بالبريد يدويًّا."
                action={
                  <Link className="btn btn-primary" to="/hr/recruit/jobs">
                    الوظائف
                  </Link>
                }
              />
            ) : (
              <div className="rc-funnel">
                {s.stages.map((st) => (
                  <Link
                    key={st.key}
                    to={`/hr/recruit/pipeline?stage=${st.key}`}
                    className="rc-funnel-row"
                  >
                    <span className="rc-funnel-label">{st.label}</span>
                    <span className="rc-funnel-track">
                      <span
                        className={`rc-funnel-bar tone-${st.kind}`}
                        style={{ width: `${Math.round((st.count / peak) * 100)}%` }}
                      />
                    </span>
                    <span className="rc-funnel-num num">{num(st.count)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <h2 className="card-title">ما الذي يُنتظر منك</h2>
            <div className="rc-next">
              {/*
                ما يُنتظر فعلُه يُقرأ من ضبط الشركة لا من كتالوجٍ ثابت.
                كان الكتالوجُ هو المصدر، فكان كلُّ عمودٍ أضافته الشركةُ
                يختفي من هذه القائمة مهما تكدّس فيه — وهي القائمةُ التي
                يُفتح عليها القسمُ كلَّ صباح.
              */}
              {s.stages
                .filter((st) => st.count > 0)
                .map((st) => (
                  <Link
                    key={st.key}
                    to={`/hr/recruit/pipeline?stage=${st.key}`}
                    className="rc-next-item"
                  >
                    <Badge kind={st.kind}>{st.label}</Badge>
                    <strong>
                      {st.stopped
                        ? 'انقل من فيه — العمود موقوف'
                        : (st.action ?? 'افتح العمود')}
                    </strong>
                    <span className="tiny mute">
                      {plural(st.count, WORDS.application)}
                    </span>
                  </Link>
                ))}
              {s.stages.every((st) => st.count === 0) && (
                <p className="tiny mute">لا شيء ينتظر — المسار خالٍ.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   حملات التعيين
   ========================================================================== */

export function Campaigns() {
  const { atLeast } = useAuth();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CampaignRow | null>(null);

  const list = useQuery({
    queryKey: ['recruit', 'campaigns'],
    queryFn: () => api<CampaignRow[]>('/hr/recruit/campaigns'),
  });

  const rows = list.data ?? [];

  return (
    <div className="page">
      <PageHead
        title="حملات التعيين"
        subtitle="مظلّة زمنية تجمع عدّة وظائف بهدفٍ وتاريخ"
        actions={
          atLeast('MANAGER') && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              حملة جديدة
            </button>
          )
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      <Notice title="لماذا الحملة" icon="target">
        «كم عيّنّا هذا الربع» سؤالٌ يخصّ الحملة، و«كم متقدّمًا على هذه
        الوظيفة» سؤالٌ يخصّ الوظيفة. الفصل بينهما يجعل كلَّ رقمٍ يُقرأ وحده.
      </Notice>

      {list.isPending && <div className="skeleton" style={{ height: 140 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا حملات بعد"
          text="الحملة اختيارية — يمكن فتح وظيفةٍ بلا حملة. وهي تفيد حين تُفتح عدّة وظائف لهدفٍ واحد."
          action={
            atLeast('MANAGER') ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أنشئ حملة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الحملة</th>
                  <th>المدة</th>
                  <th>الوظائف</th>
                  <th>الشواغر</th>
                  <th>الطلبات</th>
                  <th>الهدف</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link
                        className="cell-strong link"
                        to={`/hr/recruit/campaigns/${c.id}`}
                      >
                        {c.name}
                      </Link>
                      {c.purpose && <div className="tiny mute">{c.purpose}</div>}
                      {c.channels.length > 0 && (
                        <div className="tiny mute">
                          {c.channels
                            .map((k) => HIRING_CHANNEL_MAP[k].label)
                            .join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="cell-mute num">
                      {date(c.startsOn)}
                      {c.endsOn ? ` — ${date(c.endsOn)}` : ' — مفتوحة'}
                    </td>
                    <td className="num">{num(c.jobCount)}</td>
                    <td className="num">{num(c.openings)}</td>
                    <td className="num">{num(c.applications)}</td>
                    <td className="num">{num(c.goal)}</td>
                    <td>
                      <Badge kind={c.statusKind}>{c.statusLabel}</Badge>
                    </td>
                    <td>
                      {atLeast('MANAGER') && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setEditing(c)}
                        >
                          تعديل
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <CampaignDialog
          campaign={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function CampaignDialog({
  campaign,
  onClose,
}: {
  campaign: CampaignRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(campaign?.name ?? '');
  const [goal, setGoal] = useState(String(campaign?.goal ?? 1));
  const [startsOn, setStartsOn] = useState(
    campaign?.startsOn ?? new Date().toISOString().slice(0, 10),
  );
  const [endsOn, setEndsOn] = useState(campaign?.endsOn ?? '');
  const [status, setStatus] = useState(campaign?.status ?? 'PLANNED');
  const [notes, setNotes] = useState(campaign?.notes ?? '');
  const [purpose, setPurpose] = useState(campaign?.purpose ?? '');
  const [budget, setBudget] = useState(
    campaign?.budget === null || campaign?.budget === undefined
      ? ''
      : String(campaign.budget),
  );
  const [channels, setChannels] = useState<HiringChannel[]>(
    campaign?.channels ?? [],
  );
  const [ownerId, setOwnerId] = useState(campaign?.ownerId ?? '');

  const owners = useQuery({
    queryKey: ['hr', 'employees', 'options'],
    queryFn: () =>
      api<{ items: { id: string; name: string }[] }>('/hr/employees?pageSize=200'),
  });

  const valid = name.trim().length >= 2 && startsOn !== '';

  const save = useMutation({
    mutationFn: () =>
      campaign
        ? api(`/hr/recruit/campaigns/${campaign.id}`, {
            method: 'PATCH',
            body: {
              name: name.trim(),
              goal: Number(goal) || 1,
              startsOn,
              endsOn: endsOn || null,
              status,
              notes: notes.trim() || null,
              purpose: purpose.trim() || null,
              budget: budget === '' ? null : Number(budget),
              channels,
              ownerId: ownerId || null,
            },
          })
        : api('/hr/recruit/campaigns', {
            method: 'POST',
            body: {
              name: name.trim(),
              goal: Number(goal) || 1,
              startsOn,
              endsOn: endsOn || null,
              notes: notes.trim() || null,
              purpose: purpose.trim() || null,
              budget: budget === '' ? null : Number(budget),
              channels,
              ownerId: ownerId || null,
            },
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recruit'] });
      toast(campaign ? 'حُفظت الحملة' : 'أُنشئت الحملة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={campaign ? `تعديل ${campaign.name}` : 'حملة تعيين جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="cmp-name">
            اسم الحملة
          </label>
          <input
            id="cmp-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="توسعة فريق الإنتاج — الربع الأول"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="cmp-goal">
            عدد التعيينات المستهدفة
          </label>
          <input
            id="cmp-goal"
            className="input num"
            type="number"
            min={1}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
        </div>

        {campaign && (
          <div className="field">
            <label className="label" htmlFor="cmp-status">
              الحالة
            </label>
            <select
              id="cmp-status"
              className="select"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as CampaignRow['status'])
              }
            >
              {HIRING_CAMPAIGN_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {HIRING_CAMPAIGN_STATUS_MAP[s].label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label className="label req" htmlFor="cmp-from">
            تبدأ
          </label>
          <input
            id="cmp-from"
            className="input num"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="cmp-to">
            تنتهي
          </label>
          <input
            id="cmp-to"
            className="input num"
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
          <span className="tiny mute">اتركه فارغًا لحملةٍ مفتوحة.</span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="cmp-purpose">
            الحاجة التي فُتحت لأجلها
          </label>
          <textarea
            id="cmp-purpose"
            className="input"
            rows={2}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="افتتاح فرع المعادي — ثلاثة باعةٍ ومشرف."
          />
          {/*
            غيرُ الملاحظات: تلك مفكّرةٌ يُكتب فيها ما طرأ، وهذه سببُ الحملة.
            وحملةٌ بلا سببٍ مكتوبٍ تُقيَّم بعد ثلاثة أشهرٍ ولا أحدَ يذكر
            لماذا فُتحت أصلًا.
          */}
          <span className="tiny mute">
            تُقرأ حين تُقيَّم الحملة — وبلا كتابتها لا يُذكر بعد ثلاثة أشهرٍ
            لماذا فُتحت.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="cmp-budget">
            الميزانية المرصودة
          </label>
          <input
            id="cmp-budget"
            className="input num"
            type="number"
            min={0}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="اتركها فارغةً إن لم تُرصد"
          />
          {/*
            رقمُ التخطيط لا المصروفُ الفعليّ: ربطُ الاثنين يحتاج مركزَ
            تكلفةٍ للحملة نفسِها، وذاك لم يُبنَ — فيُقال ولا يُدَّعى.
          */}
          <span className="tiny mute">
            بها تُقسَم كلفةُ التعيين الواحد. وهي رقمُ التخطيط لا المصروفُ
            الفعليّ — المصروفُ يُسجَّل في الحسابات ولا يُربط بالحملة بعد.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="cmp-owner">
            من يقودها
          </label>
          <select
            id="cmp-owner"
            className="select"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
          >
            <option value="">بلا مسؤول</option>
            {(owners.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <span className="label">قنوات النشر</span>
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
            {HIRING_CHANNEL_DEFS.map((c) => (
              <label
                key={c.key}
                className="row tiny"
                style={{ gap: 6 }}
                title={c.description}
              >
                <input
                  type="checkbox"
                  checked={channels.includes(c.key)}
                  onChange={() =>
                    setChannels((prev) =>
                      prev.includes(c.key)
                        ? prev.filter((x) => x !== c.key)
                        : [...prev, c.key],
                    )
                  }
                />
                {c.label}
              </label>
            ))}
          </div>
          {/*
            القناةُ هنا خطّةٌ تُكتب قبل النشر، وما وقع يُقرأ من مصدر كلّ
            مرشّح. والقياسُ يمرّ بالرابط: لكلّ قناةٍ رابطُها في صفحة الحملة.
          */}
          <span className="tiny mute">
            لكلّ قناةٍ تُعلَّم هنا رابطُ تقديمٍ خاصٌّ في صفحة الحملة، ومن يقدّم
            منه يُسجَّل مصدرُه تلقائيًّا — فتُقاس القناةُ بلا أن يُسأل المتقدّم
            «من أين عرفتَ عنّا؟».
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="cmp-notes">
            ملاحظات
          </label>
          <textarea
            id="cmp-notes"
            className="input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   استمارات التقديم
   ========================================================================== */

export function ApplicationForms() {
  const { atLeast } = useAuth();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FormRow | null>(null);

  const list = useQuery({
    queryKey: ['recruit', 'forms'],
    queryFn: () => api<FormRow[]>('/hr/recruit/forms'),
  });

  const rows = list.data ?? [];

  return (
    <div className="page">
      <PageHead
        title="استمارات التقديم"
        subtitle="ما يملؤه المتقدّم — تُضبط حقولها هنا لا في الشيفرة"
        actions={
          atLeast('MANAGER') && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              استمارة جديدة
            </button>
          )
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      <Notice title="لماذا أكثر من استمارة" icon="clipboard">
        وظيفة المحاسب تسأل عن رقم القيد، ووظيفة المصمّم تسأل عن رابط
        الأعمال. واستمارةٌ واحدةٌ ثابتة تعني إمّا أسئلةً لا تعني المتقدّم
        أو أسئلةً ناقصةً على من يفرز.
      </Notice>

      {list.isPending && <div className="skeleton" style={{ height: 140 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا استمارات بعد"
          text="الوظيفة بلا استمارة تسأل عن الاسم والهاتف والبريد فقط."
          action={
            atLeast('MANAGER') ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أنشئ استمارة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="rc-forms">
          {rows.map((f) => (
            <FormCard
              key={f.id}
              form={f}
              canEdit={atLeast('MANAGER')}
              onEdit={() => setEditing(f)}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <FormDialog
          form={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * بطاقةُ الاستمارة.
 *
 * ومعها روابطُ الوظائف التي تستعملها: الاستمارةُ لا رابطَ علنيًّا لها
 * وحدَها — ما يُفتح هو صفحةُ وظيفةٍ تعرض تفاصيلَها ثمّ تسأل أسئلةَ هذه
 * الاستمارة. ومن يبحث عن «رابط الاستمارة» يبحث في الحقيقة عن أحد هذه.
 */
function FormCard({
  form: f,
  canEdit,
  onEdit,
}: {
  form: FormRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const session = useSession();
  const toast = useToast();

  return (
    <div className="rc-form-card">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <strong>{f.name}</strong>
        {f.isDefault && <Badge kind="brand">الافتراضية</Badge>}
        {!f.isActive && <Badge kind="neutral">موقوفة</Badge>}
        {f.announcement && <Badge kind="success">لها منشور</Badge>}
        <span style={{ marginInlineStart: 'auto' }}>
          {canEdit && (
            <button className="btn btn-sm" onClick={onEdit}>
              <Icon name="edit" size={13} />
              تحرير
            </button>
          )}
        </span>
      </div>

      <span className="tiny mute">
        {plural(f.fields.length, WORDS.field)} · {plural(f.jobCount, WORDS.job)}
      </span>

      {f.fields.length > 0 && (
        <div className="rc-form-peek">
          {f.fields.slice(0, 6).map((x) => (
            <span key={x.id} className="rc-chip">
              {x.label}
            </span>
          ))}
          {f.fields.length > 6 && (
            <span className="rc-chip">+{f.fields.length - 6}</span>
          )}
        </div>
      )}

      {f.jobs.length > 0 && (
        <div className="mt-2">
          <span className="tiny mute">روابط الوظائف التي تستعملها:</span>
          <ul style={{ margin: 0, paddingInlineStart: '1.1rem' }}>
            {f.jobs.map((j) => {
              const url = jobApplyUrl(
                window.location.origin,
                session.tenant.slug,
                j.slug,
              );
              return (
                <li key={j.id} className="tiny">
                  <Link className="link" to={`/hr/recruit/jobs/${j.id}`}>
                    {j.title}
                  </Link>
                  {j.status !== 'OPEN' && (
                    <span className="mute"> — الرابط لا يقبل تقديمًا الآن</span>
                  )}{' '}
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(url);
                      toast('نُسخ الرابط');
                    }}
                  >
                    نسخ الرابط
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * منشور التعيين.
 *
 * ## ولماذا نصٌّ يُنسخ لا زرُّ نشر
 *
 * لا تكاملَ مع فيسبوك ولا لينكدإن ولا غيرِهما: كلٌّ منها يحتاج موافقةَ
 * تطبيقٍ ومفاتيحَ لكلّ شركةٍ على حدة، ولا واحدةَ منها مربوطة. وزرٌّ اسمه
 * «انشر» لا ينشر أسوأُ من غيابه. فالمنشورُ يُكتب هنا ويُنسخ بنقرة، ويُلصق
 * حيث شاء صاحبُه.
 *
 * ## ولماذا مسوّدةٌ تُصاغ من الوظيفة
 *
 * كتابةُ المنشور من فراغٍ تُنتج إعلانًا ينسى الراتبَ أو المكان أو الرابط —
 * وأشهرُ عيوب إعلانات التوظيف أنّها تصف الشركةَ ولا تقول ماذا ستعمل.
 * فالمسوّدةُ تضع الحقائقَ بالترتيب الذي يقرأ به الناسُ إعلانًا، ثمّ تُحرَّر
 * بلغة من يكتبها.
 */
function AnnouncementEditor({
  value,
  onChange,
  jobs,
  draftFrom,
  onDraftFromChange,
  tenantSlug,
  companyName,
}: {
  value: string;
  onChange: (v: string) => void;
  jobs: { id: string; title: string; slug: string; status: string }[];
  draftFrom: string;
  onDraftFromChange: (v: string) => void;
  tenantSlug: string;
  companyName: string;
}) {
  const toast = useToast();

  const draft = useMutation({
    mutationFn: (id: string) => api<JobFile>(`/hr/recruit/jobs/${id}`),
    onSuccess: (job) => {
      onChange(
        draftAnnouncement({
          companyName,
          title: job.title,
          department: job.department,
          employmentLabel: job.employmentLabel,
          workModeLabel: job.workModeLabel,
          location: job.location,
          description: job.description,
          duties: job.duties,
          techRequirements: job.techRequirements,
          benefits: job.benefits,
          experienceMin: job.experienceMin,
          educationLabel: job.educationLabel,
          /* ما لا يُعلَن في الصفحة لا يُكتب في المنشور — المنشورُ أعلنُ منها. */
          salary: job.showSalary
            ? { min: job.salaryMin, max: job.salaryMax }
            : null,
          closesOn: job.closesOn ? date(job.closesOn) : null,
          applyUrl: jobApplyUrl(window.location.origin, tenantSlug, job.slug),
        }),
      );
      toast('كُتبت المسوّدة — حرّرها كما تشاء');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الصياغة'),
  });

  return (
    <div className="field span-2">
      <span className="label">منشور التعيين</span>
      <span className="tiny mute">
        ما يُنشر خارج النظام ليُقنع القارئَ بفتح الرابط — غيرُ النصّ أعلاه،
        فذاك يُقرأ داخل الصفحة بعد أن يفتحها.
      </span>

      <div className="row mt-2" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        {jobs.length > 0 ? (
          <>
            <select
              className="select"
              value={draftFrom}
              onChange={(e) => onDraftFromChange(e.target.value)}
              aria-label="الوظيفة التي يُصاغ منها المنشور"
              style={{ flex: 1, minWidth: '12rem' }}
            >
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => draftFrom && draft.mutate(draftFrom)}
              disabled={!draftFrom || draft.isPending}
            >
              <Icon name="zap" size={14} />
              {draft.isPending ? 'جارٍ…' : 'اكتب مسوّدة'}
            </button>
          </>
        ) : (
          <span className="tiny mute">
            لا وظيفةَ تستعمل هذه الاستمارة بعد — اربطها بوظيفةٍ من شاشة
            الوظائف، ثمّ تُصاغ المسوّدةُ من ملفّها.
          </span>
        )}

        {value.trim() !== '' && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard.writeText(value);
              toast('نُسخ المنشور');
            }}
          >
            <Icon name="paperclip" size={14} />
            نسخ
          </button>
        )}
      </div>

      <textarea
        className="input mt-2"
        rows={10}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={MAX_ANNOUNCEMENT}
        placeholder="نبحث عن…"
        aria-label="نصّ منشور التعيين"
      />

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="tiny mute">
          {num(value.length)} من {num(MAX_ANNOUNCEMENT)} حرفًا
        </span>
        {/*
          الحدُّ يُقال ولا يُوعَد بما لا يقع: لا نشرَ تلقائيًّا ولا جدولةَ
          نشر، والنسخُ واللصقُ هو الطريق.
        */}
        <span className="tiny mute">
          لا نشرَ تلقائيًّا — انسخه والصقه حيث تنشر.
        </span>
      </div>

      {value.trim() !== '' && (
        <div className="mt-2">
          <span className="label">كما سيُقرأ</span>
          {/*
            المعاينةُ بالخطّ نفسِه وبالأسطر كما كُتبت: منشورٌ يبدو في مربّع
            تحريرٍ مرصوصًا يظهر عند اللصق بأسطرٍ مبعثرة.
          */}
          <div
            className="card"
            style={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.9,
              maxHeight: '22rem',
              overflowY: 'auto',
            }}
          >
            {value}
          </div>
        </div>
      )}
    </div>
  );
}

interface DraftField {
  key?: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  required: boolean;
  hint: string | null;
  isActive: boolean;
}

/**
 * محرّر الاستمارة.
 *
 * الحقول تُحفظ دفعةً واحدة لا حقلًا حقلًا: المحرّر يحذف ويعيد الترتيب
 * ويضيف في جلسةٍ واحدة، وحفظُ ذلك على عدّة طلباتٍ يترك الاستمارة نصفَ
 * محفوظةٍ إن انقطع الاتصال.
 */
function FormDialog({
  form,
  onClose,
}: {
  form: FormRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const session = useSession();

  const [name, setName] = useState(form?.name ?? '');
  const [intro, setIntro] = useState(form?.intro ?? '');
  const [announcement, setAnnouncement] = useState(form?.announcement ?? '');
  const [draftFrom, setDraftFrom] = useState(form?.jobs?.[0]?.id ?? '');
  const [isDefault, setIsDefault] = useState(form?.isDefault ?? false);
  const [isActive, setIsActive] = useState(form?.isActive ?? true);
  const [fields, setFields] = useState<DraftField[]>(
    (form?.fields ?? []).map((f) => ({
      key: f.key,
      label: f.label,
      type: f.type as CustomFieldType,
      options: f.options,
      required: f.required,
      hint: f.hint,
      isActive: f.isActive,
    })),
  );

  const valid =
    name.trim().length >= 2 && fields.every((f) => f.label.trim().length > 0);

  function patchField(i: number, changes: Partial<DraftField>) {
    setFields((prev) => prev.map((f, j) => (i === j ? { ...f, ...changes } : f)));
  }

  function moveField(i: number, delta: number) {
    setFields((prev) => {
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      const [row] = next.splice(i, 1);
      next.splice(j, 0, row!);
      return next;
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        intro: intro.trim() || null,
        announcement: announcement.trim() || null,
        isDefault,
        fields: fields.map((f, i) => ({
          ...(f.key ? { key: f.key } : {}),
          label: f.label.trim(),
          type: f.type,
          options: f.type === 'SELECT' ? f.options.filter(Boolean) : [],
          required: f.required,
          hint: f.hint?.trim() || null,
          position: i,
          isActive: f.isActive,
        })),
      };
      return form
        ? api(`/hr/recruit/forms/${form.id}`, {
            method: 'PATCH',
            body: { ...body, isActive },
          })
        : api('/hr/recruit/forms', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recruit'] });
      toast(form ? 'حُفظت الاستمارة' : 'أُنشئت الاستمارة');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={form ? `تعديل ${form.name}` : 'استمارة تقديم جديدة'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="frm-name">
            اسم الاستمارة
          </label>
          <input
            id="frm-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="استمارة الوظائف الإبداعية"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="frm-intro">
            نصّ يُعرض فوق الاستمارة
          </label>
          <textarea
            id="frm-intro"
            className="input"
            rows={2}
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder="عرّف بالشركة، أو اكتب ما تريده من المتقدّم قبل أن يبدأ."
          />
        </div>

        <AnnouncementEditor
          value={announcement}
          onChange={setAnnouncement}
          jobs={form?.jobs ?? []}
          draftFrom={draftFrom}
          onDraftFromChange={setDraftFrom}
          tenantSlug={session.tenant.slug}
          companyName={session.tenant.name}
        />

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            تُقترح على كلّ وظيفةٍ جديدة
          </label>
        </div>

        {form && (
          <div className="field">
            <label className="row tiny" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              مفعَّلة
            </label>
          </div>
        )}
      </div>

      <h3 className="card-title mt-4">
        الحقول{' '}
        <span className="tiny mute">
          {plural(fields.length, WORDS.field)} من {MAX_FORM_FIELDS}
        </span>
      </h3>

      <p className="tiny mute">
        الاسم والهاتف والبريد تُسأل دائمًا ولا تُضاف هنا — هي هويّة المرشّح
        لا سؤالٌ من أسئلة الوظيفة.
      </p>

      {fields.length === 0 && (
        <p className="tiny mute mt-2">
          لا حقول بعد. الاستمارة بلا حقول تسأل عن الهويّة وحدها، وهي كافيةٌ
          لوظيفةٍ بسيطة.
        </p>
      )}

      <div className="rc-fields">
        {fields.map((f, i) => (
          <div className="rc-field-row" key={i}>
            <div className="rc-field-grip">
              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                onClick={() => moveField(i, -1)}
                disabled={i === 0}
                aria-label="أعلى"
              >
                <Icon name="chevronUp" size={14} />
              </button>
              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                onClick={() => moveField(i, 1)}
                disabled={i === fields.length - 1}
                aria-label="أسفل"
              >
                <Icon name="chevronDown" size={14} />
              </button>
            </div>

            <input
              className="input"
              value={f.label}
              onChange={(e) => patchField(i, { label: e.target.value })}
              placeholder="نصّ السؤال"
            />

            <select
              className="select"
              value={f.type}
              onChange={(e) =>
                patchField(i, { type: e.target.value as CustomFieldType })
              }
            >
              {CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CUSTOM_FIELD_TYPE_MAP[t].label}
                </option>
              ))}
            </select>

            {f.type === 'SELECT' && (
              <input
                className="input"
                value={f.options.join('، ')}
                onChange={(e) =>
                  patchField(i, {
                    options: e.target.value
                      .split(/[،,]/)
                      .map((x) => x.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="الخيارات مفصولة بفاصلة"
              />
            )}

            <label className="row tiny" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={f.required}
                onChange={(e) => patchField(i, { required: e.target.checked })}
              />
              مطلوب
            </label>

            <button
              type="button"
              className="btn btn-icon btn-ghost btn-sm"
              onClick={() => setFields((p) => p.filter((_, j) => j !== i))}
              aria-label="حذف الحقل"
            >
              <Icon name="trash" size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn mt-2"
        disabled={fields.length >= MAX_FORM_FIELDS}
        onClick={() =>
          setFields((p) => [
            ...p,
            {
              label: '',
              type: 'TEXT',
              options: [],
              required: false,
              hint: null,
              isActive: true,
            },
          ])
        }
      >
        <Icon name="plus" size={14} />
        حقل جديد
      </button>

      {form && form.fields.length > 0 && (
        <p className="tiny mute mt-2">
          حذفُ حقلٍ لا يمحو ما أجاب به من تقدّم قبله — تبقى إجابته في ملفّه
          باسم الحقل الخام.
        </p>
      )}
    </Modal>
  );
}
